import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getStrapiMediaUrl } from '../../../lib/strapi';
import { useProducts } from '../../../lib/strapi/hooks';

const RodentsProductsPage = () => {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  
  // Obtener los productos
  const { products, loading, error } = useProducts('roedores');
  
  // Subcategorías para productos de roedores
  const subCategories = [
    { id: 'all', name: 'Todos' },
    { id: 'alimentos', name: 'Alimentos' },
    { id: 'heno', name: 'Heno y Forraje' },
    { id: 'jaulas', name: 'Jaulas y Hábitats' },
    { id: 'accesorios', name: 'Accesorios' },
    { id: 'juguetes', name: 'Juguetes' },
    { id: 'sustratos', name: 'Sustratos' },
    { id: 'higiene', name: 'Higiene' }
  ];
  
  // Filtrar productos cuando cambia el filtro activo
  useEffect(() => {
    if (products) {
      if (activeFilter === 'all') {
        setFilteredProducts(products);
      } else {
        const filtered = products.filter((product: any) => 
          product.subcategory === activeFilter || 
          product.tags?.includes(activeFilter)
        );
        setFilteredProducts(filtered);
      }
    }
  }, [activeFilter, products]);
  
  // Variantes para animaciones
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };
  
  // Formatear precio
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(price);
  };
  
  // Obtener la imagen del producto
  const getProductImage = (product: any) => {
    if (!product.image) return '/placeholder-product.jpg';
    if (typeof product.image === 'string') return product.image;
    return getStrapiMediaUrl(product.image);
  };
  
  return (
    <div className="py-16 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Encabezado de la página */}
        <div className="text-center mb-16">
          <motion.h1 
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Productos para Roedores
          </motion.h1>
          <motion.p 
            className="text-xl text-gray-500 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Todo lo necesario para el cuidado y bienestar de tus pequeñas mascotas
          </motion.p>
        </div>
        
        {/* Banner de característica destacada */}
        <motion.div 
          className="mb-16 relative overflow-hidden rounded-2xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-gradient-to-r from-green-600 to-green-800 px-8 py-16 md:py-20 text-white relative z-10">
            <div className="max-w-2xl ml-0 md:ml-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Alimentación especializada</h2>
              <p className="text-lg mb-8">
                Nuestra selección de alimentos para roedores está formulada específicamente para cubrir 
                las necesidades nutricionales de hámsters, cobayas, conejos y más.
              </p>
              <a 
                href="#products" 
                className="inline-block px-6 py-3 bg-white text-green-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Explorar alimentos
              </a>
            </div>
          </div>
          {/* Imagen decorativa */}
          <div className="absolute top-0 right-0 w-1/2 h-full hidden md:block">
            <img 
              src="/categories/rodent-nutrition.jpg" 
              alt="Nutrición para roedores" 
              className="w-full h-full object-cover object-left"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = 'https://placehold.co/600x400/EAECEF/7A8699?text=Nutrición+Roedores';
              }}
            />
          </div>
        </motion.div>
        
        {/* Filtros de subcategoría */}
        <div className="mb-10 overflow-x-auto whitespace-nowrap pb-3">
          <div className="inline-flex space-x-2">
            {subCategories.map((subCat) => (
              <button
                key={subCat.id}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === subCat.id
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setActiveFilter(subCat.id)}
              >
                {subCat.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Lista de productos */}
        <div id="products" className="pb-10">
          {loading ? (
            // Skeleton para carga
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                <div key={item} className="bg-white rounded-lg overflow-hidden shadow animate-pulse">
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    <div className="h-8 bg-gray-200 rounded mt-4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            // Mensaje de error
            <div className="text-center py-12">
              <p className="text-red-500 mb-4">No pudimos cargar los productos. Por favor, intenta de nuevo más tarde.</p>
              <button 
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                onClick={() => window.location.reload()}
              >
                Reintentar
              </button>
            </div>
          ) : filteredProducts.length === 0 ? (
            // No hay productos en esta categoría
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No se encontraron productos en esta categoría.</p>
              <button 
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                onClick={() => setActiveFilter('all')}
              >
                Ver todos los productos
              </button>
            </div>
          ) : (
            // Grid de productos
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            >
              {filteredProducts.map((product) => (
                <motion.div
                  key={product.id}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-lg overflow-hidden shadow hover:shadow-lg transition-shadow duration-300"
                  data-testid="product-card"
                >
                  <Link to={`/products/${product.handle || product.slug}`}>
                    <div className="h-48 overflow-hidden">
                      <img
                        src={getProductImage(product)}
                        alt={product.title || product.name}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = 'https://placehold.co/600x400/EAECEF/7A8699?text=Producto';
                        }}
                        data-testid="product-image"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-gray-800 font-medium mb-1 text-sm" data-testid="product-name">
                        {product.title || product.name}
                      </h3>
                      <p className="text-gray-500 text-xs mb-3 line-clamp-2">
                        {product.description ? product.description.substring(0, 100) + '...' : ''}
                      </p>
                      <div className="flex justify-between items-center">
                        <span className="text-green-600 font-bold" data-testid="product-price">
                          {product.price ? formatPrice(product.price) : 'Consultar precio'}
                        </span>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                          En stock
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
        
        {/* Sección informativa */}
        <div className="mt-16 bg-gray-50 p-8 rounded-xl">
          <h2 className="text-2xl font-bold mb-6">Consejos para el cuidado de roedores</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2">Hábitat adecuado</h3>
              <p className="text-gray-600">
                Proporciona un espacio amplio y seguro con áreas para esconderse, ejercitarse y descansar.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2">Dieta balanceada</h3>
              <p className="text-gray-600">
                Mezcla de semillas, pellets nutritivos, verduras frescas y heno de calidad según la especie.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2">Enriquecimiento ambiental</h3>
              <p className="text-gray-600">
                Juguetes, túneles y elementos para roer que estimulen su actividad física y mental.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RodentsProductsPage; 